import { PLANS } from '../../server/lib/constants'
import { userIsAtLeast } from './user'

/**
 * The single source of truth for "what plan is this user on?".
 *
 * Plan precedence is subtle and used to be re-derived ad hoc at every call site,
 * which let two screens disagree — most visibly a Team subscriber showing as
 * "Pro" on /pricing (SCI-2765). All of the rules live here: pure, framework-free
 * and unit-tested. UI reads the named booleans below instead of reimplementing
 * the precedence (and getting it subtly wrong).
 *
 * @param user        the redux `ui.user` object
 * @param orgLicense  the redux `ui.organizationLicense` object
 */
export const resolvePlanState = (user, orgLicense) => {
  const plan = user?.subscription?.plan ?? PLANS.free
  // A canceled (inactive) sub owns no live plan, so it must not mark any tier as
  // current — every card stays purchasable so the user can re-subscribe.
  const hasCanceled = user?.subscription?.status === 'inactive'
  const hasLicense = !!orgLicense?.hasLicense
  // The backend has shipped this flag as both camelCase and snake_case.
  // eslint-disable-next-line camelcase
  const isSelfServe = !!(orgLicense?.isSelfServe ?? orgLicense?.is_self_serve)
  // A non-self-serve org license is Enterprise/institutional: their plan floors
  // up to Pro/organizational, but they aren't a self-serve Pro/Team purchaser.
  const isEnterprise = hasLicense && !isSelfServe
  // A self-serve team surfaces two ways, and they have DIFFERENT cancel semantics:
  //
  //   1. Via the self-serve org license (members, and the admin). Access comes
  //      from the license, not the personal sub — and joining a team flips a
  //      member's own subscription to `inactive`. So this path must NOT honour the
  //      `hasCanceled` guard, or those members would lose their Team plan on the
  //      pricing page (SCI-2765 review). A lapsed license (status INACTIVE) no
  //      longer grants access, so it stops counting as Team.
  //   2. Via the solo `team` subscription plan (the buyer, before/without a
  //      provisioned license). That's a personal sub, so the cancel guard applies.
  //
  // PLAN_LEVELS.team === PLAN_LEVELS.pro, so userIsAtLeast(user, PLANS.pro) can
  // gate features but CANNOT tell Pro from Team — read the plan and license here.
  const hasTeamLicense = hasLicense && isSelfServe && orgLicense?.status !== 'INACTIVE'
  const isTeam = hasTeamLicense || (!hasCanceled && plan === PLANS.team)
  const isPro = !hasCanceled && !isEnterprise && !isTeam && userIsAtLeast(user, PLANS.pro)
  const isBasic = !hasCanceled && !isPro && !isTeam && userIsAtLeast(user, PLANS.basic)

  return {
    plan,
    hasCanceled,
    hasLicense,
    isSelfServe,
    isEnterprise,
    isTeam,
    isPro,
    isBasic,
    isTeamAdmin: !!orgLicense?.isTeamAdmin,
    // Admin contact on a non-self-serve (Enterprise/institutional) license. Derived
    // server-side in checkLicense from adminContacts, which never reaches the client.
    isEnterpriseAdmin: !!orgLicense?.isEnterpriseAdmin
  }
}

// API access + PAYG is a self-serve Pro/Team entitlement (SCI-3608).
// Enterprise/institutional (non-self-serve) licenses are intentionally excluded —
// their API access is negotiated separately. Takes the object from
// resolvePlanState(); `isPro` already excludes Team and Enterprise, so OR-ing in
// `isTeam` gives exactly "Pro or Team, not Enterprise".
export const planUnlocksApi = ({ isPro, isTeam } = {}) => !!(isPro || isTeam)
